import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ToasterService } from '../../../service/common/toaster.service';
import { HttpClientModule } from '@angular/common/http';
import { CommonService, CrudComponent } from 'wire-frame';

import { TagModule } from 'primeng/tag';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { DropdownModule } from 'primeng/dropdown';
import { ToolbarModule } from 'primeng/toolbar';
import { EditorModule } from 'primeng/editor';
import { DialogModule } from 'primeng/dialog';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { TableModule } from 'primeng/table';
import { Table } from 'primeng/table';
import { ChipModule } from 'primeng/chip';
import { ButtonModule } from 'primeng/button';

import { MedicineService } from '../../../service/admin/medicine.service';
import {
  getMedicineCodeScaleLabel,
  isMedicineCodeInUse,
  MedicineCodeSuggestion,
  normalizeMedicineCode,
  suggestNextMedicineCode,
} from '../shared/medicine-code.util';

interface Medicine {
  id?: string;
  name: string;
  potency: string;
  code: string;
}

@Component({
  selector: 'app-medicine',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    TagModule,
    ToggleButtonModule,
    DropdownModule,
    ToolbarModule,
    CrudComponent,
    EditorModule,
    DialogModule,
    ScrollPanelModule,
    TableModule,
    ChipModule,
    ButtonModule,
  ],
  providers: [CommonService, MedicineService],
  templateUrl: './medicine.component.html',
  styleUrls: ['./medicine.component.scss']
})
export class MedicineComponent implements OnInit {
  @ViewChild('dt') dt!: Table;
  
  // A–Z filter setup
  alphabet: string[] = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));
  letters: string[] = [];
  selectedLetter: string = 'All';
  searchTerm: string = '';

  // All medicines (flattened: one potency per row)
  medicines: Medicine[] = [];
  filteredMedicines: Medicine[] = [];

  // Dialog control
  displayDialog = false;
  codeError = false;
  codeSuggestionLoading = false;
  codeManuallyEdited = false;
  codeSuggestion: MedicineCodeSuggestion | null = null;
  private codeSuggestionTimer: ReturnType<typeof setTimeout> | null = null;

  // New/selected medicine object
  medicine: Medicine = { name: '', potency: '', code: '' };
  
  // Properties for edit/delete actions
  editRecord: any = null;
  openDeletePopup: any = null;
  visible: boolean = false;
  deleteDialogVisible = false;
  deleteInProgress = false;
  medicineToDelete: Medicine | null = null;

  // ====== POTENCY DROPDOWN PROPERTIES ======
  potencyScales = [
    { label: 'Mother Tincture', value: 'mother' },
    { label: 'Decimal (X)', value: 'decimal' },
    { label: 'Centesimal (C)', value: 'centesimal' },
    { label: 'LM Potencies', value: 'lm' }
  ];

  potencyMap: { [key: string]: string[] } = {
    'mother': ['Ø', 'Q', '1X'],
    'decimal': [
        '2X','3X','4X','5X','6X','8X','10X','12X','15X','20X',
        '30X','50X','100X','200X','500X','1000X'
    ],
    'centesimal': [
        '3C','6C','12C','18C','24C','30C','50C','100C','200C',
        '1M','2M','5M','10M','20M','30M','50M','CM','DM','MM'
    ],
    'lm': [
        'LM1', 'LM2', 'LM3', 'LM4', 'LM5', 'LM6',
        'LM7', 'LM8', 'LM9', 'LM10', 'LM11', 'LM12',
        'LM13', 'LM14', 'LM15', 'LM16', 'LM17', 'LM18',
        'LM19', 'LM20', 'LM21', 'LM22', 'LM23', 'LM24',
        'LM25', 'LM26', 'LM27', 'LM28', 'LM29', 'LM30'
    ]
  };

  commonPotencies: string[] = ['30C', '200C', '1M', '6C', '12C'];

  selectedScale: string | null = null;
  availablePotencies: string[] = [];

  constructor(
    private toast: ToasterService,
    private commonService: CommonService,
    private medicineService: MedicineService
  ) {}

  ngOnInit(): void {
    this.letters = ['All', '#', ...this.alphabet];
    this.loadMedicines();
  }

  /** Initialize medicines from the service */
  loadMedicines(): void {
    this.medicineService.getMedicine().subscribe((res: any) => {
      this.medicines = res.data as any[];
      this.applyFilter();
      if (this.displayDialog && !this.medicine.id) {
        this.refreshCodeSuggestion(false);
      }
    });
  }

  /** Apply letter filter */
  applyFilter(): void {
    const term = (this.searchTerm || '').trim().toLowerCase();
    const sel = this.selectedLetter.toUpperCase();

    this.filteredMedicines = this.medicines.filter((m: Medicine) => {
      const name = String(m?.name || '');
      const potency = String(m?.potency || '');
      const code = String(m?.code || '');

      const letterOk =
        this.selectedLetter === 'All' ||
        (this.selectedLetter === '#'
          ? !/^[A-Z]/i.test(name)
          : name.toUpperCase().startsWith(sel));

      const searchOk =
        !term ||
        name.toLowerCase().includes(term) ||
        potency.toLowerCase().includes(term) ||
        code.toLowerCase().includes(term);

      return letterOk && searchOk;
    });
  }

  /** Letter selection */
  selectLetter(letter: string): void {
    this.selectedLetter = letter;
    this.applyFilter();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.applyFilter();
    if (this.dt) {
      this.dt.reset();
    }
  }

  clearLetter(): void {
    this.selectedLetter = 'All';
    this.applyFilter();
  }

  clearFilters(): void {
    this.selectedLetter = 'All';
    this.searchTerm = '';
    this.applyFilter();
    if (this.dt) {
      this.dt.reset();
    }
  }

  get totalCount(): number {
    return this.medicines.length;
  }

  get filteredCount(): number {
    return this.filteredMedicines.length;
  }

  get isCreateMode(): boolean {
    return !this.medicine?.id;
  }

  get codeMatchesSuggestion(): boolean {
    if (!this.codeSuggestion?.suggested_code) {
      return false;
    }

    return normalizeMedicineCode(this.medicine.code) === normalizeMedicineCode(this.codeSuggestion.suggested_code);
  }

  get codeSuggestionScaleLabel(): string {
    if (!this.selectedScale && this.isCreateMode) {
      return 'Select potency scale';
    }

    if (!this.codeSuggestion || this.codeSuggestion.needs_scale) {
      return 'Enter name & scale';
    }

    if (
      (this.selectedScale === 'lm' || this.selectedScale === 'mother') &&
      !this.codeSuggestion.suggested_code
    ) {
      return 'Enter medicine name';
    }

    return getMedicineCodeScaleLabel(this.codeSuggestion.scale);
  }

  get codeSuggestionPending(): boolean {
    return !this.selectedScale || !!this.codeSuggestion?.needs_scale ||
      ((this.selectedScale === 'lm' || this.selectedScale === 'mother') && !this.medicine?.name?.trim());
  }

  /** Close the main form dialog */
  closeForm() {
    this.displayDialog = false;
    this.selectedScale = null;
    this.availablePotencies = [];
    this.codeSuggestion = null;
    this.codeManuallyEdited = false;
    this.codeError = false;
    this.clearCodeSuggestionTimer();
  }

  /** Open add dialog */
  openDialog(): void {
    this.medicine = { name: '', potency: '', code: '' };
    this.codeError = false;
    this.codeManuallyEdited = false;
    this.codeSuggestion = null;
    this.selectedScale = null;
    this.availablePotencies = [];
    this.displayDialog = true;
    this.refreshCodeSuggestion(false);
  }

  onMedicineNameChange(name: string): void {
    this.medicine.name = name;
    if (!this.isCreateMode) {
      return;
    }

    this.scheduleCodeSuggestionRefresh();
  }

  onCodeInputChange(code: string): void {
    this.medicine.code = code;
    this.codeError = false;

    if (!this.codeSuggestion?.suggested_code) {
      this.codeManuallyEdited = !!normalizeMedicineCode(code);
      return;
    }

    this.codeManuallyEdited =
      normalizeMedicineCode(code) !== normalizeMedicineCode(this.codeSuggestion.suggested_code);
  }

  applySuggestedCode(): void {
    if (!this.codeSuggestion?.suggested_code) {
      return;
    }

    this.medicine.code = this.codeSuggestion.suggested_code;
    this.codeManuallyEdited = false;
    this.codeError = false;
  }

  private scheduleCodeSuggestionRefresh(): void {
    this.clearCodeSuggestionTimer();
    this.applyLocalCodeSuggestion();

    this.codeSuggestionTimer = setTimeout(() => {
      this.refreshCodeSuggestion(false);
    }, 250);
  }

  private clearCodeSuggestionTimer(): void {
    if (this.codeSuggestionTimer) {
      clearTimeout(this.codeSuggestionTimer);
      this.codeSuggestionTimer = null;
    }
  }

  private applyLocalCodeSuggestion(): void {
    const suggestion = suggestNextMedicineCode(
      this.medicine.name,
      this.medicines,
      this.selectedScale,
      this.medicine.potency,
    );
    this.codeSuggestion = suggestion;

    if (this.isCreateMode && !this.codeManuallyEdited) {
      this.medicine.code = suggestion.suggested_code || '';
    }
  }

  private refreshCodeSuggestion(showLoading = true): void {
    if (!this.displayDialog || !this.isCreateMode) {
      return;
    }

    this.applyLocalCodeSuggestion();

    if (!this.selectedScale) {
      return;
    }

    if (showLoading) {
      this.codeSuggestionLoading = true;
    }

    this.medicineService.suggestMedicineCode(this.medicine.name, {
      potency: this.medicine.potency,
      scale: this.selectedScale ?? undefined,
    }).subscribe({
      next: (res: any) => {
        this.codeSuggestionLoading = false;
        if (!res?.success || !res?.data) {
          return;
        }

        this.codeSuggestion = res.data as MedicineCodeSuggestion;
        if (!this.codeManuallyEdited) {
          this.medicine.code = this.codeSuggestion.suggested_code || '';
        }
      },
      error: () => {
        this.codeSuggestionLoading = false;
      },
    });
  }

  selectPotencyScale(value: string): void {
    this.selectedScale = value;
    this.onScaleChange();
    if (this.isCreateMode) {
      this.scheduleCodeSuggestionRefresh();
    }
  }

  getPotencyScaleHint(value: string): string {
    switch (value) {
      case 'mother':
        return 'Ø, Q, 1X';
      case 'decimal':
        return '2X – 1000X';
      case 'centesimal':
        return '3C – MM';
      case 'lm':
        return 'LM1 – LM30';
      default:
        return '';
    }
  }

  // ====== POTENCY DROPDOWN METHODS ======
  onScaleChange(): void {
    if (this.selectedScale) {
      this.availablePotencies = this.potencyMap[this.selectedScale];
      // Reset potency when scale changes (unless we're initializing from existing data)
      if (!this.medicine.id || !this.medicine.potency) {
        this.medicine.potency = '';
      }
    } else {
      this.availablePotencies = [];
      this.medicine.potency = '';
    }
  }

  onPotencyChange(potency: string): void {
    this.medicine.potency = potency;
    if (this.isCreateMode) {
      this.scheduleCodeSuggestionRefresh();
    }
  }

  selectQuickPotency(potency: string): void {
    this.medicine.potency = potency;
    // Automatically set the scale based on the potency
    if (potency === 'Ø' || potency === 'Q' || potency === '1X') {
      this.selectedScale = 'mother';
    } else if (potency.endsWith('X')) {
      this.selectedScale = 'decimal';
    } else if (potency.startsWith('LM') || /^0\/\d+$/.test(potency)) {
      this.selectedScale = 'lm';
    } else {
      this.selectedScale = 'centesimal';
    }
    this.onScaleChange();
    if (this.isCreateMode) {
      this.scheduleCodeSuggestionRefresh();
    }
  }

  setScaleFromPotency(potency: string): void {
    if (!potency) return;
    
    if (potency === 'Ø' || potency === 'Q' || potency === '1X') {
      this.selectedScale = 'mother';
    } else if (potency.endsWith('X')) {
      this.selectedScale = 'decimal';
    } else if (potency.startsWith('LM') || /^0\/\d+$/.test(potency)) {
      this.selectedScale = 'lm';
    } else {
      this.selectedScale = 'centesimal';
    }
    this.onScaleChange();
  }
  
  /** Decide whether to add or update a medicine */
  processMedicine() {
    const name = (this.medicine.name ?? '').trim();
    const potency = (this.medicine.potency ?? '').trim();
    const code = normalizeMedicineCode(this.medicine.code ?? '');

    if (!name || !potency) {
      this.toast.add({ severity: 'warn', summary: 'Incomplete', detail: 'Name and potency are required.' });
      return;
    }

    if (!code) {
      this.toast.add({ severity: 'warn', summary: 'Code Required', detail: 'Medicine code is required.' });
      return;
    }

    this.medicine.code = code;

    if (isMedicineCodeInUse(code, this.medicines, this.medicine.id)) {
      this.codeError = true;
      this.toast.add({
        severity: 'error',
        summary: 'Duplicate Code',
        detail: `Code ${code} is already assigned to another medicine.`,
      });
      return;
    }

    this.codeError = false;
    
    if (this.medicine?.id) {
      this.updateMedicine();
    } else {
      this.addMedicine();
    }
  }

  addMedicine() {
    this.medicineService
      .addMedicine(this.medicine)
      .subscribe({
        next: (res: any) => {
          // Keep local list fresh so the next LM potency reuses LMA43-N immediately
          // even before the full reload finishes.
          const created = res?.data;
          if (created?.code) {
            this.medicines = [...this.medicines, created];
            this.applyFilter();
          }

          this.toast.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Medicine Created Successfully',
          });
          this.closeForm();
          this.loadMedicines();
        },
        error: (err) => {
          const duplicateCode = !!err?.error?.errors?.code?.length;
          this.codeError = duplicateCode;
          this.toast.add({
            severity: 'error',
            summary: duplicateCode ? 'Duplicate Code' : 'Save Failed',
            detail: duplicateCode
              ? 'This code is already in use. Use the suggested code instead.'
              : err?.error?.message || 'Unable to create medicine.',
          });

          if (duplicateCode) {
            this.codeManuallyEdited = false;
            this.refreshCodeSuggestion(false);
          }
        },
      });
  }

  updateMedicine() {
    this.medicineService
      .updateMedicine(this.medicine)
      .subscribe({
        next: () => {
          this.toast.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Medicine updated Successfully.',
          });
          this.closeForm();
          this.loadMedicines();
        },
        error: (err) => {
          const duplicateCode = !!err?.error?.errors?.code?.length;
          this.codeError = duplicateCode;
          this.toast.add({
            severity: 'error',
            summary: duplicateCode ? 'Duplicate Code' : 'Update Failed',
            detail: duplicateCode
              ? 'This code is already in use.'
              : err?.error?.message || 'Unable to update medicine.',
          });
        },
      });
  }

  closeDeleteDialog(): void {
    if (this.deleteInProgress) {
      return;
    }

    this.deleteDialogVisible = false;
    this.resetDeleteDialogState();
  }

  onDeleteDialogHide(): void {
    this.resetDeleteDialogState();
  }

  confirmDeleteMedicine(): void {
    if (!this.medicineToDelete || this.deleteInProgress) {
      return;
    }

    const target = this.medicineToDelete;
    this.deleteInProgress = true;

    this.medicineService.deleteMedicine(target).subscribe({
      next: () => {
        this.toast.add({
          severity: 'success',
          summary: 'Deleted',
          detail: `${target.name} was removed from the medicine master.`,
        });
        this.deleteDialogVisible = false;
        this.resetDeleteDialogState();
        this.closeForm();
        this.loadMedicines();
      },
      error: (err) => {
        this.deleteInProgress = false;
        this.toast.add({
          severity: 'error',
          summary: 'Delete Failed',
          detail: err?.error?.message || 'Unable to delete medicine.',
        });
      },
    });
  }

  private resetDeleteDialogState(): void {
    this.medicineToDelete = null;
    this.deleteInProgress = false;
  }
  
  /** Handle actions from a generic component (like a CRUD table) */
  handleEvent(event: any) {
    if (event.action.tooltip == 'Update Medicine') {
        this.medicine = { ...event.data };
        this.codeManuallyEdited = true;
        this.codeError = false;
        this.codeSuggestion = null;
        // Set the scale based on existing potency when editing
        if (this.medicine.potency) {
          this.setScaleFromPotency(this.medicine.potency);
        }
        this.displayDialog = true;
    } else if (event.action.tooltip == 'Delete Medicine') {
        this.editRecord = event.data;
        this.delRecPopupOpen({
            title: this.editRecord?.['name'],
            obj: this.editRecord,
        });
    } else if (event.action.tooltip == 'Dispense Medicine') {
        this.medicine = event.data;
        // console.log('Dispense Medicine', this.medicine);
        this.showDialog();
    }
  }

  /** Show a generic dialog (can be repurposed) */
  showDialog() {
    this.visible = true;
  }

  /** Open the delete confirmation popup */
  delRecPopupOpen(item: any) {
    this.medicineToDelete = item.obj ?? null;
    this.deleteInProgress = false;
    this.deleteDialogVisible = !!this.medicineToDelete;
  }
}
